import { useEffect, useState, useMemo, useCallback } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { observer } from 'mobx-react-lite'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { Close } from 'blaze-ui/icons'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { LinkExternal } from 'blaze-ui/icons/20/LinkExternal'
import { trackEventInHeap, HeapEventType } from 'utils/heapTracking'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useCreditUsage } from 'hooks/useCreditUsage'
import { useShowCreditsPaywall } from 'hooks/useShowCreditsPaywall'
import { PurchaseContentCreditsModal } from 'components/Settings/TieredBilling/components/AutopilotDetails/components/PurchaseContentCreditsModal/PurchaseContentCreditsModal'
import { CountInput } from 'components/Wizards/CountInput/CountInput'
import { Accordion } from 'components/Accordion/Accordion'
import { useAccordion } from 'components/Accordion/useAccordion'
import { FeaturedMedia } from 'types'
import { ContentPlanValues } from '../VisualStylesForExistingUsersModal'
import { ContentTypesSelector, MediaWeights } from 'screens/ContentPreferences/components/ContentTypesSelector'
import { getContentsCount } from 'screens/BrandKits/components/BrandPlan/utils'
import { useContentPreferencesFields } from 'screens/BrandKits/components/ContentPreferences/useContentPreferencesFields'
import Styles from '../VisualStylesForExistingUsersModal.module.scss'
import { useCampaignsEnabled } from 'hooks/queries/brandPlans/useCampaignsEnabled'
import { useIsPioneerTrial } from 'hooks/useIsPioneerTrial'
import { useOpenAutopilotUpgrade } from 'hooks/useOpenAutopilotUpgrade'

interface ContentPlanStepProps {
  animatedImagesOptIn: boolean
  onContentPlanChange: (values: ContentPlanValues) => void
  onNext: () => void
  onBack: () => void
  onClose: () => void
}

export const ContentPlanStep = observer((props: ContentPlanStepProps) => {
  const { animatedImagesOptIn, onContentPlanChange, onNext, onBack, onClose } = props
  const { openModal } = useModals()
  const { data: brandPlan } = useCurrentBrandPlan()
  const isCampaignsAccount = useCampaignsEnabled()
  const { usageCosts, creditsRemaining } = useCreditUsage()
  const { price } = useShowCreditsPaywall()
  const { expanded, handleHeaderClick, animationActive } = useAccordion(true, true)
  const isPioneerTrial = useIsPioneerTrial()
  const openAutopilotUpgrade = useOpenAutopilotUpgrade()

  // Get initial values from brand plan
  const initialSocialPosts = useMemo(
    () => getContentsCount(brandPlan?.crosspost_contents ?? []),
    [brandPlan?.crosspost_contents],
  )
  const initialShortFormVideos = useMemo(
    () => getContentsCount(brandPlan?.video_crosspost_contents ?? []),
    [brandPlan?.video_crosspost_contents],
  )
  const initialBlogPosts = useMemo(() => getContentsCount(brandPlan?.seo_contents ?? []), [brandPlan?.seo_contents])
  const initialEmails = useMemo(() => getContentsCount(brandPlan?.email_contents ?? []), [brandPlan?.email_contents])

  // Get initial featured media and weights from brand kit content preferences
  const brandKit = brandPlan?.brand_kit
  const { preferences } = useContentPreferencesFields({ brandKit })

  const initialFeaturedMedia = preferences.featured_media
  const initialMediaWeights = useMemo(
    () => ({
      video: preferences.featured_media_video_weight,
      image: preferences.featured_media_image_weight,
      text: preferences.featured_media_text_weight,
    }),
    [
      preferences.featured_media_video_weight,
      preferences.featured_media_image_weight,
      preferences.featured_media_text_weight,
    ],
  )

  const [socialPostsCount, setSocialPostsCount] = useState(initialSocialPosts || 3)
  const [shortFormVideosCount, setShortFormVideosCount] = useState(initialShortFormVideos || 2)
  const [blogPostsCount, setBlogPostsCount] = useState(initialBlogPosts || 0)
  const [emailsCount, setEmailsCount] = useState(initialEmails || 0)
  const [featuredMedia, setFeaturedMedia] = useState<FeaturedMedia[]>(initialFeaturedMedia as FeaturedMedia[])
  const [mediaWeights, setMediaWeights] = useState<MediaWeights>(initialMediaWeights)

  // Update state when brand plan loads
  // Apply minimum thresholds for users with low/no content
  useEffect(() => {
    if (brandPlan) {
      // If social posts is 0-2, default to 3
      const socialDefault = initialSocialPosts <= 2 ? 3 : initialSocialPosts
      setSocialPostsCount(socialDefault)

      // If short-form is 0-1, default to 2
      const shortFormDefault = initialShortFormVideos <= 1 ? 2 : initialShortFormVideos
      setShortFormVideosCount(shortFormDefault)
      if (initialBlogPosts >= 0) setBlogPostsCount(initialBlogPosts)
      if (initialEmails >= 0) setEmailsCount(initialEmails)
    }
  }, [brandPlan, initialSocialPosts, initialShortFormVideos, initialBlogPosts, initialEmails])

  // Credit costs
  const baseContentCreditCost = usageCosts?.content_credits_cost ?? 0
  const videoCreditCost = animatedImagesOptIn ? usageCosts?.video_credits_cost ?? 0 : baseContentCreditCost
  const stylizedImageCost = usageCosts?.visual_styles_credits_cost ?? 0
  const textTemplateCost = baseContentCreditCost
  const blogEmailCost = baseContentCreditCost

  // Calculate total weekly credit cost
  const totalWeeklyCost = useMemo(() => {
    let socialCost = 0

    if (featuredMedia.includes('video')) {
      const videoPercentage = mediaWeights.video / 100
      socialCost += Math.ceil(socialPostsCount * videoPercentage * videoCreditCost)
      socialCost += Math.ceil(socialPostsCount * videoPercentage * stylizedImageCost)
    }
    if (featuredMedia.includes('image')) {
      const imagePercentage = mediaWeights.image / 100
      socialCost += Math.ceil(socialPostsCount * imagePercentage * stylizedImageCost)
    }
    if (featuredMedia.includes('text')) {
      const textPercentage = mediaWeights.text / 100
      socialCost += Math.ceil(socialPostsCount * textPercentage * textTemplateCost)
    }

    const shortFormCost = shortFormVideosCount * videoCreditCost + shortFormVideosCount * stylizedImageCost
    const blogCost = blogPostsCount * blogEmailCost
    const emailCost = emailsCount * blogEmailCost

    return socialCost + shortFormCost + blogCost + emailCost
  }, [
    socialPostsCount,
    shortFormVideosCount,
    blogPostsCount,
    emailsCount,
    featuredMedia,
    mediaWeights,
    videoCreditCost,
    stylizedImageCost,
    textTemplateCost,
    blogEmailCost,
  ])

  const handleContentTypesUpdate = useCallback((newFeaturedMedia: string[], newWeights: MediaWeights) => {
    setFeaturedMedia(newFeaturedMedia as FeaturedMedia[])
    setMediaWeights(newWeights)
  }, [])

  useEffect(() => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'view-content-plan-step',
    })
  }, [])

  const handleNext = () => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'complete-content-plan-step',
    })
    onContentPlanChange({
      socialPostsCount,
      shortFormVideosCount,
      blogPostsCount,
      emailsCount,
      featuredMedia,
      mediaWeights,
    })

    if (isPioneerTrial) {
      trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
        action: 'open-upgrade-modal-pioneer-trial',
      })
      openAutopilotUpgrade({ newStack: true })
      return
    }

    if (creditsRemaining < totalWeeklyCost && price) {
      trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
        action: 'open-add-credits-modal',
      })
      openModal(PurchaseContentCreditsModal, {
        price,
        onSuccess: () => {
          trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
            action: 'add-credits-success',
          })
          onNext()
        },
        defaultBatchCount: Math.ceil(totalWeeklyCost / 100),
      })
      return
    }

    onNext()
  }

  return (
    <Modal.Content className={Styles.contentPlanContent}>
      <Button size="sm" variant="tertiary" onPress={onClose} className={Styles.stepCloseButton}>
        <Close />
      </Button>

      <div className={Styles.contentPlanInner}>
        <div className={Styles.contentPlanHeader}>
          <Heading level={1}>{isCampaignsAccount ? 'Set your campaign plan' : 'Set your content plan'}</Heading>
          <Text color="var(--dark-40)" variant="primary">
            These changes will update your content plan and switch your posting method to crosspost. You can adjust
            these settings anytime.
          </Text>
        </div>

        <div className={Styles.contentPlanSections}>
          {/* Social Media Posts Section */}
          <div className={Styles.contentPlanSection}>
            <div className={Styles.contentPlanSectionLeft}>
              <Text variant="primary" fontWeight="500">
                Social Media Posts
              </Text>
            </div>
            <div className={Styles.contentPlanSectionRight}>
              <Text variant="secondary">Crossposts per week</Text>
              <CountInput
                name="socialPostsCount"
                value={socialPostsCount}
                onChange={setSocialPostsCount}
                min={0}
                max={15}
                size="sm"
                buttonSize="sm"
                buttonVariant="secondary"
                applyRedesign
              />
            </div>
          </div>

          {/* Content Type Format Selector */}
          <div className={Styles.contentPlanContentTypes}>
            <Text variant="secondary">Choose content type format</Text>
            <ContentTypesSelector
              featuredMedia={featuredMedia}
              weights={mediaWeights}
              onUpdate={handleContentTypesUpdate}
              hideLabel
              creditCosts={{
                video: `${videoCreditCost} credits per ${animatedImagesOptIn ? 'AI video' : 'video template'}`,
                image: `${stylizedImageCost} credits per stylized image`,
                text: `${textTemplateCost} credits per text template`,
              }}
            />
          </div>

          {/* Short-form Videos Section */}
          <div className={Styles.contentPlanSection}>
            <div className={Styles.contentPlanSectionLeft}>
              <Text variant="primary" fontWeight="500">
                Short-form Videos
              </Text>
              <div className={Styles.creditCostItem}>
                <Credits size={14} color="var(--dark-40)" />
                <Text variant="metadata" color="var(--dark-40)">
                  {videoCreditCost} credits per {animatedImagesOptIn ? 'AI video' : 'video'}
                </Text>
              </div>
            </div>
            <div className={Styles.contentPlanSectionRight}>
              <Text variant="secondary">Crossposts per week</Text>
              <CountInput
                name="shortFormVideosCount"
                value={shortFormVideosCount}
                onChange={setShortFormVideosCount}
                min={0}
                max={15}
                size="sm"
                buttonSize="sm"
                buttonVariant="secondary"
                applyRedesign
              />
            </div>
          </div>

          {/* Blog Posts Section */}
          <div className={Styles.contentPlanSection}>
            <div className={Styles.contentPlanSectionLeft}>
              <Text variant="primary" fontWeight="500">
                Blog Posts
              </Text>
              <div className={Styles.creditCostItem}>
                <Credits size={14} color="var(--dark-40)" />
                <Text variant="metadata" color="var(--dark-40)">
                  {blogEmailCost} credits per blog post
                </Text>
              </div>
            </div>
            <div className={Styles.contentPlanSectionRight}>
              <CountInput
                name="blogPostsCount"
                value={blogPostsCount}
                onChange={setBlogPostsCount}
                min={0}
                max={15}
                size="sm"
                buttonSize="sm"
                buttonVariant="secondary"
                applyRedesign
              />
            </div>
          </div>

          {/* Emails Section */}
          <div className={Styles.contentPlanSection}>
            <div className={Styles.contentPlanSectionLeft}>
              <Text variant="primary" fontWeight="500">
                Emails
              </Text>
              <div className={Styles.creditCostItem}>
                <Credits size={14} color="var(--dark-40)" />
                <Text variant="metadata" color="var(--dark-40)">
                  {blogEmailCost} credits per email
                </Text>
              </div>
            </div>
            <div className={Styles.contentPlanSectionRight}>
              <CountInput
                name="emailsCount"
                value={emailsCount}
                onChange={setEmailsCount}
                min={0}
                max={15}
                size="sm"
                buttonSize="sm"
                buttonVariant="secondary"
                applyRedesign
              />
            </div>
          </div>

          {/* FAQ Accordion */}
          <div className={Styles.contentPlanFaq}>
            <Accordion
              expanded={expanded}
              onHeaderClick={handleHeaderClick}
              animationActive={animationActive}
              allowCollapsingAll
              className={Styles.faqAccordion}
            >
              <Accordion.Pane title="What uses credits?">
                <div className={Styles.faqContent}>
                  <Text variant="secondary">{baseContentCreditCost} credits per Brand Kit and Stock Content</Text>
                  <Text variant="secondary">{stylizedImageCost} credits per Stylized Image</Text>
                  <Text variant="secondary">{videoCreditCost} credits per AI Video</Text>
                </div>
              </Accordion.Pane>
              <Accordion.Pane title="How much do credits cost?">
                <Text variant="secondary">Credit pricing: 100 credits = $10</Text>
              </Accordion.Pane>
            </Accordion>
            <hr className={Styles.faqDivider} />
            <a
              href="https://help.blaze.ai/credits"
              target="_blank"
              rel="noopener noreferrer"
              className={Styles.learnMoreLink}
            >
              <Text variant="secondary" fontWeight="500" color="var(--dark-80)">
                Learn More About Credits
              </Text>
              <LinkExternal size={16} color="var(--dark-80)" />
            </a>
          </div>
        </div>
      </div>

      <div className={Styles.contentPlanFooter}>
        <div className={Styles.footerSide}>
          <Button variant="tertiary" size="lg" onPress={onBack}>
            Back
          </Button>
        </div>
        <Text variant="secondary">This content frequency will cost {totalWeeklyCost} credit per week</Text>
        <div className={Styles.footerSide}>
          <Button variant="primary" size="lg" onPress={handleNext}>
            {isPioneerTrial ? (
              'Upgrade'
            ) : (
              <>
                <span className={Styles.addCreditsButtonText}>Add Credits</span>
                <Credits size={16} color="var(--light-100)" />
                <span className={Styles.totalWeeklyCost}>{totalWeeklyCost}</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal.Content>
  )
})
